import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import chatService from '../../services/chatService';

const formatThread = (history) =>
  (history || []).map((chat) => ({
    id: chat.id,
    text: chat.message,
    sender: chat.sender,
    admin_name: chat.admin_name,
    timestamp: new Date(chat.timestamp || chat.created_at)
  }));

const Chatbot = ({ member }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const reloadMessages = useCallback(async () => {
    if (!member?.id) return;
    try {
      const chatHistory = await chatService.getMemberChatHistory(member.id);
      setMessages(formatThread(chatHistory));
    } catch (error) {
      console.error('Chatbot: reload failed', error);
    }
  }, [member?.id]);

  useEffect(() => {
    reloadMessages();
  }, [reloadMessages]);

  useEffect(() => {
    const interval = setInterval(reloadMessages, 4000);
    const onFocus = () => reloadMessages();
    const onReply = () => reloadMessages();
    window.addEventListener('focus', onFocus);
    window.addEventListener('adminReplyAdded', onReply);
    window.addEventListener('newChatMessage', onReply);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('adminReplyAdded', onReply);
      window.removeEventListener('newChatMessage', onReply);
    };
  }, [reloadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    const optimistic = {
      id: `tmp-${Date.now()}`,
      text: userMessage,
      sender: 'member',
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const memberId = typeof member.id === 'string' ? parseInt(member.id, 10) : member.id;
      await chatService.addMemberMessage(
        memberId,
        member.name || 'Member',
        member.uan || '',
        userMessage
      );
      await reloadMessages();
      toast.success('Sent to all admins');
    } catch (error) {
      console.error('Chatbot: Error saving message:', error);
      toast.error(error?.response?.data?.message || 'Failed to send message');
      await reloadMessages();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-5rem)] sm:h-[min(720px,calc(100vh-8rem))] w-full max-w-5xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="bg-primary-600 text-white px-4 sm:px-5 py-3.5 flex items-center gap-3 shrink-0">
        <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
          <MessageSquare className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold leading-tight">Contact Admin</h2>
          <p className="text-xs text-primary-100 truncate">Your message reaches all admins</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-3 bg-slate-50">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full px-6">
            <div className="text-center text-slate-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">No messages yet</p>
              <p className="text-xs mt-1">
                Send a message — every admin will see it in notifications.
              </p>
            </div>
          </div>
        ) : (
          messages.map((message) => {
            const isMine = message.sender === 'member';
            return (
              <div key={message.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[90%] sm:max-w-[70%] rounded-2xl px-3.5 py-2.5 shadow-sm ${
                    isMine
                      ? 'bg-primary-600 text-white rounded-br-md'
                      : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                  }`}
                >
                  {!isMine && (
                    <div className="text-[11px] font-semibold text-primary-700 mb-0.5">
                      {message.admin_name
                        ? `Replied by: ${message.admin_name}`
                        : 'Admin'}
                    </div>
                  )}
                  <p className="text-sm leading-snug whitespace-pre-wrap break-words">{message.text}</p>
                  <div className={`text-[10px] text-right mt-1 ${isMine ? 'text-primary-100' : 'text-slate-400'}`}>
                    {message.timestamp.toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSend}
        className="shrink-0 bg-white border-t border-slate-200 px-3 sm:px-4 py-3"
      >
        <div className="flex items-end gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message to admin..."
            className="flex-1 min-h-[44px] px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="h-11 w-11 sm:w-auto sm:px-4 rounded-lg bg-primary-600 text-white flex items-center justify-center gap-2 shadow-sm disabled:opacity-40 shrink-0"
            aria-label="Send"
          >
            <Send className="h-5 w-5" />
            <span className="hidden sm:inline text-sm font-medium">Send</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default Chatbot;
